/**
 * The bestiary: creatures sculpted in 3D from rounded parts and projected for each camera angle.
 *
 * A creature is laid out in its own space — forward (f), sideways (l, + toward the viewer when it
 * faces right) and up (u) — then turned to one of five angles and drawn far to near, each part
 * shaded dark below and lit on top. Every pose is just a handful of numbers, so one sculpt gives
 * all five drawn views (mirrored for the other three of the eight headings) and every action frame.
 *
 * Body plans: four-legged (wolf, boar, big cats, fox spirit), serpentine (snakes, centipede),
 * arthropods (scorpion, spider) and flyers (bat).
 */
import { figOutline } from './figureOutline';
import { lazyList, lazyOne, lazyRecord } from './drawBudget';
import { box, canvas, ctx2d, ell, line, litShade } from './pixel';
import { FRAMES, type SpriteSet, type View } from './sprites';

/** Frames every creature has: the stride, standing, the combat stance, the wind-up, the blow, a hit. */
export type BeastPose = 'walk' | 'idle' | 'ready' | 'windup' | 'strike' | 'hurt';

const ALL_VIEWS: readonly View[] = ['front', 'front3', 'side', 'back3', 'back'];

/** Which way the creature faces on the ground for each camera angle, in degrees (+ = toward the viewer). */
const ANGLE: Record<View, number> = { side: 0, front3: 60, front: 90, back3: -60, back: -90 };
/**
 * How much depth shows as height on screen. Flatter than the 2:1 ground so a beast seen head-on stays
 * a beast, not a tall column of back.
 */
const SQUASH = 0.36;
/** Flyers turn less across the screen, so their spread wings never vanish edge-on. */
const FLYER_ANGLE: Record<View, number> = { side: 35, front3: 65, front: 90, back3: -65, back: -90 };

/** Shades of one material: shadow, body and lit top. */
export interface Fur {
  d: string;
  m: string;
  l: string;
}
const fur = (d: string, m: string, l: string): Fur => ({ d, m, l });

interface Part {
  f: number;
  l: number;
  u: number;
  rf: number;
  rl: number;
  ru: number;
  fur: Fur;
}
type P3 = readonly [number, number, number];

const part = (f: number, l: number, u: number, rf: number, rl: number, ru: number, m: Fur): Part => ({
  f,
  l,
  u,
  rf,
  rl,
  ru,
  fur: m,
});

/** Golden-ratio scatter, for marks that should look random but never change between frames. */
const scatter = (i: number) => (i * 0.618034) % 1;

/** One frame of a creature, seen from one camera angle. */
class Sculpt {
  readonly c: HTMLCanvasElement;
  readonly g: CanvasRenderingContext2D;
  readonly ca: number;
  readonly sa: number;
  private readonly groups: { d: number; draw: () => void }[] = [];

  constructor(
    view: View,
    w: number,
    h: number,
    private readonly cx: number,
    private readonly cy: number,
    angles: Record<View, number> = ANGLE,
  ) {
    this.c = canvas(w, h);
    this.g = ctx2d(this.c);
    const a = (angles[view] * Math.PI) / 180;
    this.ca = Math.cos(a);
    this.sa = Math.sin(a);
  }

  /** Toward-viewer depth of a point (nearer = larger). */
  depth(f: number, l: number): number {
    return f * this.sa + l * this.ca;
  }

  proj(f: number, l: number, u: number): [number, number] {
    return [this.cx + f * this.ca - l * this.sa, this.cy - u + this.depth(f, l) * SQUASH];
  }

  at(f: number, l: number, u: number): [number, number] {
    const [x, y] = this.proj(f, l, u);
    return [Math.round(x), Math.round(y)];
  }

  /** How much a surface facing (nf, nl, nu) turns toward the camera, which looks a little downward. */
  faces(nf: number, nl: number, nu = 0): number {
    return nf * this.sa + nl * this.ca + nu * 0.6;
  }

  dot(p: P3, col: string, w = 1, h = 1): void {
    const [x, y] = this.at(...p);
    box(this.g, x, y, w, h, col);
  }

  seg(a: P3, b: P3, col: string, thick = 1): void {
    const [x0, y0] = this.at(...a);
    const [x1, y1] = this.at(...b);
    line(this.g, x0, y0, x1, y1, col);
    if (thick > 1) line(this.g, x0 + 1, y0, x1 + 1, y1, col);
  }

  /** A jointed limb: segments through the points, the first ones thicker. */
  limb(pts: readonly P3[], col: string, thick = 1): void {
    for (let i = 0; i + 1 < pts.length; i++) this.seg(pts[i]!, pts[i + 1]!, col, i === 0 ? thick : 1);
  }

  /** A flat skin (a wing membrane) stretched over the points, filled scanline by scanline. */
  fill(pts: readonly P3[], col: string): void {
    const s = pts.map((p) => this.proj(...p));
    const g = this.g;
    g.fillStyle = col;
    const ys = s.map((p) => p[1]);
    for (let y = Math.floor(Math.min(...ys)); y <= Math.ceil(Math.max(...ys)); y++) {
      const yc = y + 0.5;
      const xs: number[] = [];
      for (let i = 0; i < s.length; i++) {
        const [ax, ay] = s[i]!;
        const [bx, by] = s[(i + 1) % s.length]!;
        if ((ay <= yc && by > yc) || (by <= yc && ay > yc)) xs.push(ax + ((yc - ay) / (by - ay)) * (bx - ax));
      }
      xs.sort((a, b) => a - b);
      for (let i = 0; i + 1 < xs.length; i += 2) {
        const x0 = Math.round(xs[i]!);
        const x1 = Math.round(xs[i + 1]!);
        if (x1 > x0) g.fillRect(x0, y, x1 - x0, 1);
      }
    }
  }

  private radii(p: Part): [number, number] {
    const rx = Math.hypot(p.rf * this.ca, p.rl * this.sa);
    const dz = Math.hypot(p.rf * this.sa, p.rl * this.ca) * SQUASH;
    return [rx, Math.hypot(p.ru, dz)];
  }

  /** Shade parts in passes (shadow, body, lit top) so a chain of them blends into one form. */
  shade(parts: readonly Part[]): void {
    const sorted = [...parts].sort((a, b) => this.depth(a.f, a.l) - this.depth(b.f, b.l));
    for (const pass of [0, 1, 2])
      for (const p of sorted) {
        const [x, y] = this.proj(p.f, p.l, p.u);
        const [rx, ry] = this.radii(p);
        if (pass === 0) ell(this.g, x, y, rx, ry, p.fur.d);
        else if (pass === 1 && rx > 1.2) ell(this.g, x, y - 0.7, rx - 0.9, ry - 1, p.fur.m);
        else if (pass === 2 && rx > 1.8 && ry > 1.8)
          ell(this.g, x + rx * 0.12, y - ry * 0.45, rx * 0.6, ry * 0.4, p.fur.l);
      }
  }

  /** Something drawn as one piece, ordered with the others by its depth at (f, l). */
  add(f: number, l: number, draw: () => void): void {
    this.groups.push({ d: this.depth(f, l), draw });
  }

  /** Draws every piece far to near, then the game's rim light and outline. */
  finish(): HTMLCanvasElement {
    this.groups.sort((a, b) => a.d - b.d);
    for (const gr of this.groups) gr.draw();
    return figOutline(litShade(this.c));
  }

  /** Glaring eyes (slits when hurt), on a head at `head` looking forward. */
  eyes(head: P3, spread: number, main: string, hot: string, squint: boolean, size = 1): void {
    for (const s of [1, -1]) {
      if (this.faces(0.6, 0.8 * s) < 0.05) continue;
      const [x, y] = this.at(head[0], spread * s, head[2]);
      // The inner corner is the one nearer the snout (or the middle of the face, head-on).
      const inner = this.ca > 0.3 ? 1 : x < this.at(head[0], 0, head[2])[0] ? 1 : -1;
      if (squint) {
        box(this.g, Math.min(x, x + inner), y, 2, 1, '#141018');
        continue;
      }
      box(this.g, x, y, size, size, main);
      box(this.g, x + inner * size, y, 1, size, hot);
      box(this.g, x + inner * size, y - 1, 1, 1, '#141018');
    }
  }
}

/**
 * A creature's cycles run on a phase of 0–4 (its four old steps), drawn at any point in between:
 * `turn` is the phase as an angle round the cycle, `odd` is 1 on the odd steps and 0 on the even
 * (a bob on each stride), `half` is 1 half way round and 0 at the start.
 */
const turn = (ph: number) => (ph * Math.PI) / 2;
const odd = (ph: number) => Math.sin(turn(ph)) ** 2;
const half = (ph: number) => 0.5 - 0.5 * Math.cos(turn(ph));

/**
 * The six actions of a creature for every camera angle, as a sprite set. Frames are drawn the first
 * time they are shown (a view the first time it is looked at), within the main thread's per-frame
 * budget for new art (drawBudget.ts): a creature coming into view costs a frame or two, not all
 * of its several hundred.
 */
export function beastViews(
  draw: (pose: BeastPose, ph: number, view: View) => HTMLCanvasElement,
  anchor: { ax: number; ay: number; h: number },
  post: (c: HTMLCanvasElement) => HTMLCanvasElement = (c) => c,
): SpriteSet {
  const one = (view: View): SpriteSet => {
    /** A frame of this view already drawn, to stand in while another waits its turn. */
    let any: HTMLCanvasElement | undefined;
    const d = (pose: BeastPose, ph = 0) => (any = post(draw(pose, ph, view)));
    const stand = () => any;
    // Every cycle in `FRAMES` frames, the four old steps spread through them.
    const four = (pose: BeastPose) => lazyList(FRAMES, (i) => d(pose, (i * 4) / FRAMES), stand);
    const idle = four('idle');
    const ready = four('ready');
    const strike = lazyOne(() => d('strike'), stand);
    const windup = lazyOne(() => d('windup'), stand);
    const hurt = lazyOne(() => d('hurt'), stand);
    const set = { idle, walk: four('walk'), ready, poses: {}, ...anchor } as unknown as SpriteSet;
    // The single poses, drawn when first asked for. Phase 0 of a cycle is its first frame.
    const single: [object, string, () => HTMLCanvasElement][] = [
      [set, 'atk', strike],
      [set, 'block', () => ready[0]!],
      [set, 'cast', windup],
      [set, 'sit', () => idle[0]!],
      [set, 'jump', strike],
      [set.poses, 'windup', windup],
      [set.poses, 'hurt', hurt],
      [set.poses, 'dash', strike],
    ];
    for (const [on, key, get] of single) Object.defineProperty(on, key, { get, enumerable: true });
    return set;
  };
  const base = one('front3');
  const views: Partial<Record<View, SpriteSet>> = {};
  for (const v of ALL_VIEWS.filter((v) => v !== 'front3')) {
    let s: SpriteSet | undefined;
    Object.defineProperty(views, v, { get: () => (s ??= one(v)), enumerable: true });
  }
  base.views = views;
  return base;
}

// ── Four-legged ──

/** How a four-legged creature holds itself in a frame. */
interface QuadRig {
  bob: number;
  chestU: number;
  hipU: number;
  bodyF: number;
  headF: number;
  headU: number;
  /** How far the jaws gape (0 shut, 3 wide). */
  jaw: number;
  /** Height of the raised hackles along the spine. */
  hackle: number;
  tailU: number;
  tailF: number;
  tailL: number;
  earsBack: number;
  squint: boolean;
  /** Stride offset and lift of each leg: near fore, far fore, near hind, far hind. */
  legs: [number, number][];
}

function quadRig(pose: BeastPose, ph: number): QuadRig {
  const r: QuadRig = {
    bob: 0,
    chestU: 0,
    hipU: 0,
    bodyF: 0,
    headF: 0,
    headU: 0,
    jaw: 0,
    hackle: 1,
    tailU: 0,
    tailF: 0,
    tailL: 0,
    earsBack: 0,
    squint: false,
    legs: [
      [0, 0],
      [0, 0],
      [0, 0],
      [0, 0],
    ],
  };
  if (pose === 'walk') {
    // Trot: diagonal pairs swing together, each lifted as it comes forward.
    const s = Math.cos(turn(ph)) * 2.6;
    const a = Math.max(0, -Math.sin(turn(ph))) * 1.5;
    const b = Math.max(0, Math.sin(turn(ph))) * 1.5;
    r.legs = [
      [s, a],
      [-s, b],
      [-s, b],
      [s, a],
    ];
    r.bob = odd(ph) * 0.7;
    r.headU = odd(ph) * -0.4;
    r.tailL = Math.sin(turn(ph)) * 1.2;
  } else if (pose === 'idle') {
    // Slow breathing, the tail swinging lazily.
    const br = 0.3 - 0.3 * Math.cos(turn(ph)) + 0.05 * Math.sin(turn(ph));
    r.chestU = br;
    r.headU = br * 0.5;
    r.tailL = Math.sin(turn(ph)) * 0.8;
  } else if (pose === 'ready') {
    // Stalking snarl: head low, shoulders up, hackles bristling, lips back off the fangs.
    const g = odd(ph) * 0.5;
    r.headU = -2.6 + g;
    r.headF = 0.6;
    r.chestU = -0.6;
    r.jaw = 1 + half(ph) * 0.5;
    r.hackle = 3;
    r.tailU = 2.5;
    r.tailF = -1;
    r.legs = [
      [1.8, 0],
      [-0.6, 0],
      [-0.8, 0],
      [0.6, 0],
    ];
  } else if (pose === 'windup') {
    // Coiled to spring: haunches down, head drawn back, jaws parting.
    r.hipU = -2;
    r.bodyF = -1.4;
    r.headF = -1;
    r.headU = -2;
    r.jaw = 2;
    r.hackle = 3;
    r.tailU = -1;
    r.legs = [
      [2.4, 0],
      [1.6, 0],
      [-1.4, 0],
      [-2, 0],
    ];
  } else if (pose === 'strike') {
    // The lunge: stretched out, forepaws off the ground, jaws wide.
    r.bodyF = 2.4;
    r.chestU = 1.2;
    r.headF = 3;
    r.headU = 0.6;
    r.jaw = 3;
    r.hackle = 2;
    r.tailU = 3;
    r.tailF = -1.5;
    r.earsBack = 1;
    r.legs = [
      [4, 2.5],
      [3, 1.8],
      [-2.8, 0],
      [-2, 0],
    ];
  } else {
    // Struck: head thrown up and back, ears flat, tail tucked.
    r.bodyF = -1;
    r.headF = -1.6;
    r.headU = 2;
    r.jaw = 1.2;
    r.hackle = 0;
    r.tailU = -3;
    r.tailF = 2.5;
    r.earsBack = 2;
    r.squint = true;
    r.legs = [
      [-1, 0.8],
      [-0.4, 0],
      [0.6, 0],
      [0.2, 0],
    ];
  }
  return r;
}

/** What makes one four-legged creature different from another. */
interface QuadSpec {
  fur: Fur;
  /** The legs on the far side, a step darker. */
  far: Fur;
  /** Scruff, cheek ruff and hackles. */
  mane: Fur;
  /** Throat and chest. */
  bib: Fur;
  /** Tail tip. */
  tip: Fur;
  /** Body length, leg height, body thickness, head size, muzzle length and leg thickness (wolf = 1). */
  len: number;
  tall: number;
  girth: number;
  head: number;
  snout: number;
  limbs: number;
  /** Cheek ruff size (0 = none). */
  ruff: number;
  ears: 'point' | 'round';
  ear: number;
  tail: 'bush' | 'whip' | 'tuft' | 'nine';
  /** Hackle length along the spine (0 = smooth-backed). */
  hackles: number;
  marks?: { kind: 'moon' | 'stripes' | 'spots'; col: string };
  tusks?: boolean;
  /** Pig's snout: a broad flat nose disc. */
  disc?: boolean;
  eye: readonly [string, string];
  claw: string;
  /** Canvas width and height, and where the feet stand in it. */
  frame: readonly [number, number, number, number];
}

function quadruped(S: QuadSpec, pose: BeastPose, ph: number, view: View): HTMLCanvasElement {
  const [W, H, CX, CY] = S.frame;
  const k = new Sculpt(view, W, H, CX, CY);
  const R = quadRig(pose, ph);
  const L = S.len;
  const T = S.tall;
  const G = S.girth;
  const HS = S.head;
  const bob = R.bob;
  const bf = R.bodyF;

  const hips = part(-5.5 * L + bf, 0, 9 * T + bob + R.hipU, 4.2 * L, 4.6 * G, 3.7 * G, S.fur);
  const mid = part(
    -1 * L + bf,
    0,
    9.6 * T + bob + (R.hipU + R.chestU) * 0.5,
    4.4 * L,
    4.6 * G,
    3.4 * G,
    S.fur,
  );
  const chest = part(3.4 * L + bf, 0, 9.8 * T + bob + R.chestU, 4.2 * L, 5.4 * G, 4.6 * G, S.fur);
  const scruff = part(
    6.4 * L + bf + R.headF * 0.4,
    0,
    11.6 * T + bob + R.chestU + R.headU * 0.45,
    3.2 * L,
    5 * G,
    3.8 * G,
    S.mane,
  );
  const bib = part(
    6.6 * L + bf + R.headF * 0.3,
    0,
    9.4 * T + bob + R.chestU + R.headU * 0.3,
    1.4,
    3 * G,
    2.6 * G,
    S.bib,
  );
  const hf = 9.2 * L + bf + R.headF;
  const hu = 13 * T + bob + R.chestU + R.headU;
  const skull = part(hf, 0, hu, 2.8 * HS, 3.6 * HS, 2.7 * HS, S.fur);
  const ruff = part(hf - 1.2 * HS, 0, hu - 0.8 * HS, 2 * HS, 4.8 * HS * S.ruff, 2.8 * HS, S.mane);
  const sf = hf + 0.9 * HS + 1.8 * S.snout;
  const sr = 0.4 + 1.9 * S.snout;
  const snout = part(sf, 0, hu - 0.7 * HS + R.jaw * 0.35, sr, 1.7 * HS, 1.3 * HS, S.fur);
  const jaw = part(sf - 0.4 - R.jaw * 0.2, 0, hu - 2 * HS - R.jaw * 0.6, sr * 0.83, 1.1 * HS, 0.8, S.fur);
  const gum = part(
    sf - 0.5,
    0,
    hu - 1.3 * HS - R.jaw * 0.2,
    sr * 0.74,
    HS,
    Math.max(0.6, R.jaw * 0.45),
    fur('#3a0c16', '#7a1e2a', '#a8343c'),
  );

  // Tail.
  const tl = R.tailL + 1.4;
  const hu0 = hips.u;
  const tail: Part[] = [];
  if (S.tail === 'bush') {
    tail.push(
      part(-8 * L + bf, tl * 0.3, 9.6 * T + bob + R.hipU * 0.6, 1.8, 2, 2, S.fur),
      part(
        -10.4 * L + bf + R.tailF * 0.5,
        tl * 0.6,
        8.4 * T + bob + R.tailU * 0.5 + R.hipU * 0.4,
        2.4,
        2.7,
        2.4,
        S.fur,
      ),
      part(-12.6 * L + bf + R.tailF, tl, 7 * T + bob + R.tailU, 2.1, 2.5, 2, S.tip),
    );
  } else if (S.tail === 'whip') {
    // A long cat's tail, curling up at the end.
    const pts: [number, number, number][] = [
      [-8.4, 0.2, 0.4],
      [-10.4, 0.4, -0.8],
      [-12.4, 0.6, -0.9],
      [-14.1, 0.8, 0.3],
      [-15.2, 1, 1.6],
      [-15.6, 1, 2.8],
    ];
    pts.forEach(([f, l, u], i) => {
      const t = i / (pts.length - 1);
      tail.push(
        part(
          f * L + bf + R.tailF * t,
          l * tl,
          hu0 + u + R.tailU * t,
          1.3,
          1.3,
          1.3,
          i === pts.length - 1 ? S.tip : S.fur,
        ),
      );
    });
  } else if (S.tail === 'tuft') {
    tail.push(
      part(-8.6 * L + bf, 0.2 * tl, hu0 + 0.4, 0.8, 0.8, 0.8, S.fur),
      part(-9.6 * L + bf, 0.3 * tl, hu0 - 0.8 + R.tailU * 0.3, 0.7, 0.7, 0.8, S.fur),
      part(-10.2 * L + bf + R.tailF * 0.3, 0.4 * tl, hu0 - 2 + R.tailU * 0.5, 1, 1, 1.1, S.tip),
    );
  } else {
    // Nine tails fanned out behind like a flame.
    for (let i = 0; i < 9; i++) {
      const s = (i - 4) / 4;
      const up = 1 - Math.abs(s);
      tail.push(
        part(-8 * L + bf, s * 0.8, hu0 + 0.8, 1.6, 1.6, 1.6, S.fur),
        part(
          -10.4 * L + bf + R.tailF * 0.5,
          s * 2.8 + R.tailL * 0.5,
          hu0 + 2 + up * 1.6 + R.tailU * 0.5,
          1.9,
          1.9,
          1.9,
          S.fur,
        ),
        part(
          -11.8 * L + bf + R.tailF + Math.abs(s),
          s * 4.6 + R.tailL,
          hu0 + 3.6 + up * 3 + R.tailU,
          1.7,
          1.7,
          1.7,
          S.fur,
        ),
        part(
          -12.6 * L + bf + R.tailF + Math.abs(s) * 1.4,
          s * 5.6 + R.tailL,
          hu0 + 4.8 + up * 3.8 + R.tailU,
          1.1,
          1.1,
          1.1,
          S.tip,
        ),
      );
    }
  }
  k.add(-10 * L + bf, S.tail === 'nine' ? 0 : tl * 0.5, () => k.shade(tail));

  // Legs: near fore, far fore, near hind, far hind.
  const roots: [number, number, boolean][] = [
    [4 * L + bf, 3.6 * G, true],
    [4 * L + bf, -3.6 * G, true],
    [-5.6 * L + bf, 3.3 * G, false],
    [-5.6 * L + bf, -3.3 * G, false],
  ];
  const lt = S.limbs;
  roots.forEach(([f0, l0, fore], i) => {
    const [df, lift] = R.legs[i]!;
    const m = l0 < 0 && k.ca > 0.3 ? S.far : S.fur;
    const top = fore ? chest.u - chest.ru * 0.74 : hips.u - hips.ru * 0.65;
    const parts = fore
      ? [
          part(f0 + df * 0.35, l0, top, 1.8 * lt, 1.3 * lt, 2.6 * T, m),
          part(f0 + df * 0.75, l0, 3.4 * T + lift, lt, 0.9 * lt, 2.1 * T, m),
          part(f0 + df + 0.5, l0, 1 + lift, 1.4 * lt, lt, 0.9, m),
        ]
      : [
          part(f0 + df * 0.3 + 0.3, l0, top, 2.7 * lt, 1.6 * lt, 3 * T, m),
          part(f0 + df * 0.7 - 1.3, l0, 3.2 * T + lift, 0.9 * lt, 0.9 * lt, 1.8 * T, m),
          part(f0 + df - 0.4, l0, 1 + lift, 1.4 * lt, lt, 0.9, m),
        ];
    k.add(f0, l0 * 1.6, () => {
      k.shade(parts);
      // Claws on the paw's leading edge.
      const paw = parts[2]!;
      if (k.sa > -0.4) k.dot([paw.f + paw.rf * 0.9, paw.l, paw.u - 0.4], S.claw);
    });
  });

  // Body, scruff, hackles and markings.
  const torso = [hips, mid, chest];
  const torsoAt = (f: number) => {
    const a = f < mid.f ? hips : mid;
    const b = f < mid.f ? mid : chest;
    const t = Math.max(0, Math.min(1, (f - a.f) / (b.f - a.f)));
    return { u: a.u + (b.u - a.u) * t, rl: a.rl + (b.rl - a.rl) * t, ru: a.ru + (b.ru - a.ru) * t };
  };
  k.add(mid.f, 0, () => {
    k.shade([...torso, scruff, ...(k.sa > 0 ? [bib] : [])]);
    const spine = [...torso, scruff];
    if (S.hackles > 0) {
      // The dark saddle down the spine.
      for (let i = 0; i < 3; i++) {
        const [a, b] = [spine[i]!, spine[i + 1]!];
        k.seg([a.f, 0, a.u + a.ru * 0.8], [b.f, 0, b.u + b.ru * 0.8], S.mane.m);
      }
      for (let i = 0; i < 7; i++) {
        const t0 = i / 6;
        const seg = Math.min(2, Math.floor(t0 * 3));
        const a = spine[seg]!;
        const b = spine[seg + 1]!;
        const t = t0 * 3 - seg;
        const f = a.f + (b.f - a.f) * t;
        const u = a.u + a.ru + (b.u + b.ru - a.u - a.ru) * t - 0.6;
        const h = R.hackle * S.hackles * (i % 2 ? 0.7 : 1.1) * (0.5 + t0);
        if (h < 0.5) continue;
        k.seg([f, 0, u], [f - 1 - h * 0.4, 0, u + h], S.mane.d);
        k.dot([f - 1 - h * 0.4, 0, u + h], i % 2 ? S.mane.l : S.tip.m);
      }
    }
    const mk = S.marks;
    if (mk?.kind === 'moon' && k.ca > 0.5) {
      // A pale crescent moon on the near shoulder.
      const [mx, my] = k.at(chest.f - 0.4, chest.rl * 0.95, chest.u + 0.4);
      const s = k.sa >= 0 ? 1 : -1;
      for (const [dx, dy] of [
        [1, -2],
        [0, -1],
        [0, 0],
        [1, 1],
      ])
        box(k.g, mx + dx! * s, my + dy!, 1, 1, mk.col);
    } else if (mk?.kind === 'stripes') {
      for (let f = hips.f - 2 * L; f <= chest.f + 1.5 * L; f += 2.4 * L) {
        const b = torsoAt(f);
        // Across the back, then down each flank that faces the camera.
        k.limb(
          [
            [f, -b.rl * 0.75, b.u + b.ru * 0.65],
            [f - 0.5, 0, b.u + b.ru],
            [f, b.rl * 0.75, b.u + b.ru * 0.65],
          ],
          mk.col,
        );
        for (const s of [1, -1])
          if (s * k.ca > -0.25)
            k.limb(
              [
                [f, s * b.rl * 0.85, b.u + b.ru * 0.5],
                [f + 0.7, s * b.rl * 0.95, b.u],
                [f + 0.3, s * b.rl * 0.8, b.u - b.ru * 0.5],
              ],
              mk.col,
            );
      }
    } else if (mk?.kind === 'spots') {
      for (let i = 1; i < 34; i++) {
        const f = hips.f - 2.5 * L + scatter(i) * (chest.f - hips.f + 4 * L);
        const th = (scatter(i * 7 + 3) - 0.5) * 3;
        if (k.faces(0, Math.sin(th), Math.cos(th)) < 0.1) continue;
        const b = torsoAt(f);
        k.dot([f, b.rl * Math.sin(th) * 0.9, b.u + b.ru * Math.cos(th) * 0.9], mk.col, i % 3 ? 1 : 2);
      }
    }
  });

  // Head: ruff, skull, ears, muzzle and jaws, then the face.
  k.add(hf, 0, () => {
    const E = S.ear;
    const ears = [1, -1].map((s) =>
      S.ears === 'point'
        ? part(
            hf - 0.6 * HS - R.earsBack,
            2.3 * HS * s,
            hu + 2.6 * HS - R.earsBack * 0.7,
            0.9 * E,
            0.7 * E,
            (1.8 - R.earsBack * 0.3) * E,
            S.fur,
          )
        : part(
            hf - 0.8 * HS - R.earsBack * 0.5,
            2.6 * HS * s,
            hu + 2.2 * HS - R.earsBack * 0.5,
            1.1 * E,
            0.6 * E,
            1.1 * E,
            S.fur,
          ),
    );
    const back = k.sa < -0.2;
    const ruffs = S.ruff > 0 ? [ruff] : [];
    k.shade(back ? [...ruffs, gum, jaw, snout] : ruffs);
    // Ears drawn one by one so they stay two distinct points.
    for (const e of ears.sort((a, b) => k.depth(a.f, a.l) - k.depth(b.f, b.l))) {
      k.shade([e]);
      if (S.ears === 'point') {
        const [x, y] = k.at(e.f - 0.3 * R.earsBack, e.l, e.u + e.ru + 0.6);
        box(k.g, x, y - 1, 1, 2, S.fur.d);
      }
      if (!back && R.earsBack < 2) k.dot([e.f + 0.4, e.l, e.u], '#2a1c2c');
    }
    k.shade([skull]);
    if (back) return;
    k.shade([gum, jaw]);
    k.shade([snout]);
    const tip: P3 = [snout.f + snout.rf, 0, snout.u + 0.3];
    if (S.disc) {
      // A pig's flat nose disc with its two nostrils.
      const [x, y] = k.at(...tip);
      box(k.g, x - 1, y - 1, 2, 2, '#b07a72');
      box(k.g, x - 1, y, 1, 1, '#2a1418');
      box(k.g, x, y, 1, 1, '#2a1418');
    } else {
      const [x, y] = k.at(...tip);
      box(k.g, x - (k.ca < 0.5 ? 1 : 0), y, k.ca < 0.5 ? 2 : 1, 1, '#141018');
    }
    for (const s of [1, -1]) {
      if (k.faces(0.6, 0.8 * s) < -0.3 && k.ca > 0.2) continue;
      if (S.tusks) {
        // Tusks curving up out of the lower jaw.
        const base: P3 = [snout.f + snout.rf * 0.2, s * snout.rl * 0.9, jaw.u + 0.3];
        k.seg(base, [snout.f + snout.rf * 0.7, s * (snout.rl + 0.8), snout.u + 1.4], '#efe6cc');
        k.dot([snout.f + snout.rf * 0.7, s * (snout.rl + 0.8), snout.u + 1.4], '#fffaf0');
        continue;
      }
      // Fangs: upper ones hang from the muzzle, lower ones rise when the jaws open.
      const [fx, fy] = k.at(snout.f + snout.rf * 0.55, 0.7 * s, snout.u - snout.ru + 0.2);
      box(k.g, fx, fy, 1, R.jaw > 0 ? 2 : 1, '#f4efe2');
      if (R.jaw >= 1.5) {
        const [lx, ly] = k.at(jaw.f + jaw.rf * 0.5, 0.6 * s, jaw.u + jaw.ru);
        box(k.g, lx, ly - 1, 1, 2, '#e8e2d2');
      }
    }
    k.eyes([skull.f + 1.7 * HS, 0, skull.u + 0.6 * HS], 1.7 * HS, S.eye[0], S.eye[1], R.squint);
  });

  return k.finish();
}

const quadSet = (S: QuadSpec, h: number, post?: (c: HTMLCanvasElement) => HTMLCanvasElement) =>
  beastViews((pose, ph, view) => quadruped(S, pose, ph, view), { ax: S.frame[2], ay: S.frame[3], h }, post);

/** หมาป่าเงาจันทร์: slate fur lit silver, bristling hackles and a pale crescent on the shoulder. */
const WOLF: QuadSpec = {
  fur: fur('#252740', '#4a5274', '#7d89b0'),
  far: fur('#1b1c30', '#363c5a', '#56608a'),
  mane: fur('#1f2136', '#363a5a', '#646f98'),
  bib: fur('#5a6488', '#9aa4c4', '#c4cce6'),
  tip: fur('#6a7498', '#b4bed8', '#e2e8f8'),
  len: 1,
  tall: 1,
  girth: 1,
  head: 1,
  snout: 1,
  limbs: 1,
  ruff: 1,
  ears: 'point',
  ear: 1,
  tail: 'bush',
  hackles: 1,
  marks: { kind: 'moon', col: '#d8e0fa' },
  eye: ['#ffd23a', '#ff5a1f'],
  claw: '#d8d2c2',
  frame: [46, 34, 23, 27],
};

/** หมูป่าเขี้ยวเหล็ก: a low, barrel-bodied boar with a bristle crest and iron-pale tusks. */
const BOAR: QuadSpec = {
  fur: fur('#1e1612', '#4a3628', '#7a5e46'),
  far: fur('#140e0c', '#33251c', '#55402f'),
  mane: fur('#120c0a', '#2a1e18', '#5a4636'),
  bib: fur('#2e221a', '#4a3628', '#6a5240'),
  tip: fur('#120c0a', '#2a1e18', '#4a3a2e'),
  len: 0.92,
  tall: 0.72,
  girth: 1.3,
  head: 1.15,
  snout: 1.05,
  limbs: 1.2,
  ruff: 0.7,
  ears: 'point',
  ear: 0.75,
  tail: 'tuft',
  hackles: 1.5,
  tusks: true,
  disc: true,
  eye: ['#ff4a2a', '#ffb02a'],
  claw: '#8a7a6a',
  frame: [46, 32, 23, 26],
};

/** เสือดาวเงา: a lean smoke-grey leopard dappled with black rosettes, eyes of poison jade. */
const LEOPARD: QuadSpec = {
  fur: fur('#2a2632', '#56505e', '#8a8292'),
  far: fur('#1e1a24', '#3e3946', '#5e5868'),
  mane: fur('#2a2632', '#56505e', '#8a8292'),
  bib: fur('#5e5868', '#9a92a2', '#c2bcc8'),
  tip: fur('#141018', '#24202a', '#3a3440'),
  len: 1.12,
  tall: 0.92,
  girth: 0.82,
  head: 0.92,
  snout: 0.42,
  limbs: 0.95,
  ruff: 0,
  ears: 'round',
  ear: 0.9,
  tail: 'whip',
  hackles: 0,
  marks: { kind: 'spots', col: '#16121c' },
  eye: ['#7dff7a', '#e8ffd0'],
  claw: '#e8e2d2',
  frame: [52, 36, 26, 28],
};

/** พยัคฆ์ขาวภูผา: a huge white tiger with black stripes and ice-blue eyes. */
const TIGER: QuadSpec = {
  fur: fur('#7e8298', '#cfd2de', '#f6f7fc'),
  far: fur('#62667c', '#a6aabc', '#cfd2de'),
  mane: fur('#8e92a8', '#e2e4ee', '#ffffff'),
  bib: fur('#a6aabc', '#f2f3f8', '#ffffff'),
  tip: fur('#141018', '#24202a', '#3a3440'),
  len: 1.25,
  tall: 1.08,
  girth: 1.25,
  head: 1.22,
  snout: 0.55,
  limbs: 1.3,
  ruff: 0.9,
  ears: 'round',
  ear: 1.1,
  tail: 'whip',
  hackles: 0,
  marks: { kind: 'stripes', col: '#1c1824' },
  eye: ['#8ff0ff', '#2a8cff'],
  claw: '#f4f0e4',
  frame: [62, 44, 31, 35],
};

/** จิ้งจอกเก้าหาง: a pale fox spirit with nine flame-tipped tails and violet eyes. */
const FOX_SPIRIT: QuadSpec = {
  fur: fur('#a89a8c', '#ece2d4', '#fffaf2'),
  far: fur('#8a7c70', '#c8bcae', '#e2d8ca'),
  mane: fur('#b4a698', '#f6eee2', '#ffffff'),
  bib: fur('#c4b8aa', '#fffaf2', '#ffffff'),
  tip: fur('#7a1424', '#d8324a', '#ff7a8a'),
  len: 0.86,
  tall: 0.92,
  girth: 0.74,
  head: 0.92,
  snout: 0.95,
  limbs: 0.85,
  ruff: 0.65,
  ears: 'point',
  ear: 1.45,
  tail: 'nine',
  hackles: 0,
  eye: ['#ff6ad8', '#ffe0fa'],
  claw: '#f4efe2',
  frame: [46, 38, 23, 30],
};

// ── Serpentine ──

interface SerpentSpec {
  back: Fur;
  /** Every other segment (bands and blotches). */
  band: Fur;
  /** Number of segments, their spacing and the body's thickness. */
  n: number;
  gap: number;
  r: number;
  /** How high the head rears up when calm. */
  rear: number;
  head: number;
  eye: readonly [string, string];
  /** Centipede: a pair of legs on every segment, pincers and feelers. */
  legs?: string;
  frame: readonly [number, number, number, number];
}

function serpent(S: SerpentSpec, pose: BeastPose, ph: number, view: View): HTMLCanvasElement {
  const [W, H, CX, CY] = S.frame;
  const k = new Sculpt(view, W, H, CX, CY);
  // Wave phase, how high the head rears, how far it lunges forward, how wide the mouth opens.
  let wave = (ph * Math.PI) / 2;
  let rear = S.rear;
  let lunge = 0;
  let jaw = 0;
  let squint = false;
  let amp = 1.6;
  if (pose === 'idle') {
    wave = Math.sin((ph * Math.PI) / 2) * 0.5;
    amp = 1.1;
  } else if (pose === 'ready') {
    rear = S.rear * 1.5 + odd(ph) * 0.4;
    lunge = -1;
    jaw = 0.5 + half(ph) * 0.5;
    wave = 0.6;
  } else if (pose === 'windup') {
    rear = S.rear * 1.8;
    lunge = -3;
    jaw = 2;
    amp = 2.2;
    wave = 0.9;
  } else if (pose === 'strike') {
    rear = S.rear * 0.6;
    lunge = 3.5;
    jaw = 3;
    amp = 0.7;
  } else if (pose === 'hurt') {
    rear = S.rear * 1.6;
    lunge = -2;
    jaw = 1;
    squint = true;
    wave = 1.4;
  }
  const len = S.n * S.gap;
  const f0 = len * 0.5 + lunge;
  const spine: P3[] = [];
  for (let i = 0; i < S.n; i++) {
    const s = i * S.gap;
    // Steady near the head, swinging further down the body; the front rears up.
    const sway = amp * Math.sin(s * 0.42 - wave) * Math.min(1, s / 5);
    const lift = rear * Math.max(0, 1 - s / (S.gap * 4.5)) ** 2;
    const pull = lunge < 0 ? lunge * Math.max(0, 1 - s / (S.gap * 3)) : lunge * Math.max(0, 1 - s / len);
    spine.push([f0 - s - (pull - lunge), sway, S.r + lift]);
  }
  const body = spine.slice(1).map(([f, l, u], i) => {
    const t = i / S.n;
    const r = S.r * (1 - t * 0.55);
    return part(f, l, u, r * 1.1, r, r * 0.9, i % 2 ? S.band : S.back);
  });
  const mid = spine[Math.floor(S.n / 2)]!;

  if (S.legs) {
    // Legs on each side, far ones first; each pair steps in turn down the body.
    for (const side of [1, -1]) {
      const legs: P3[][] = spine.slice(1).map(([f, l, u], i) => {
        const r = S.r * (1 - (i / S.n) * 0.55);
        const step = Math.sin(i * 1.4 + (ph * Math.PI) / 2) * (pose === 'walk' ? 0.9 : 0.3);
        return [
          [f, l + side * r * 0.8, u - r * 0.2],
          [f - 0.3 + step, l + side * (r + 1.2), u + 0.5],
          [f - 0.8 + step * 1.4, l + side * (r + 2), 0],
        ];
      });
      k.add(mid[0], side * S.r * 2.2, () => {
        for (const leg of legs) k.limb(leg, S.legs!);
      });
    }
  }
  k.add(mid[0], 0, () => k.shade(body));

  // Head, looking along its neck.
  const [hf, hl, hu] = spine[0]!;
  const HS = S.head;
  const skull = part(hf + 0.6 * HS, hl, hu + 0.3, 1.9 * HS, 1.5 * HS, 1.1 * HS, S.back);
  const lower = part(hf + 0.5 * HS - jaw * 0.2, hl, hu - 0.6 - jaw * 0.55, 1.6 * HS, 1.2 * HS, 0.6, S.band);
  const mouth = part(
    hf + 0.4 * HS,
    hl,
    hu - 0.3 - jaw * 0.25,
    1.4 * HS,
    HS,
    Math.max(0.5, jaw * 0.4),
    fur('#3a0c16', '#8a1e2e', '#b8404a'),
  );
  k.add(hf, hl, () => {
    const back = k.sa < -0.2;
    if (jaw > 0) k.shade(back ? [lower] : [mouth, lower]);
    k.shade([skull]);
    if (back) return;
    const tip: P3 = [skull.f + skull.rf, hl, skull.u - 0.2];
    if (S.legs) {
      // Curved poison pincers and long feelers.
      for (const s of [1, -1]) {
        k.limb(
          [
            [skull.f + 0.6, hl + s * 1.1, skull.u - 0.6],
            [skull.f + 2.4, hl + s * (1.6 + jaw * 0.3), skull.u - 0.8],
            [skull.f + 3, hl + s * 0.5, skull.u - 0.8],
          ],
          S.legs,
        );
        k.limb(
          [
            [skull.f + 0.6, hl + s * 0.8, skull.u + 0.8],
            [skull.f + 2.5, hl + s * 2.4, skull.u + 3.2],
            [skull.f + 3.8, hl + s * 3.4, skull.u + 3.4],
          ],
          S.band.l,
        );
      }
    } else if (jaw > 0) {
      // Fangs down from the upper jaw.
      for (const s of [1, -1]) {
        const [x, y] = k.at(skull.f + skull.rf * 0.6, hl + s * 0.6, skull.u - skull.ru);
        box(k.g, x, y, 1, 2, '#f4efe2');
      }
    } else if (pose === 'ready' || (pose === 'idle' && Math.abs(ph - 2) < 0.5)) {
      // The forked tongue flickers.
      const a = k.at(...tip);
      const b = k.at(tip[0] + 1.6, tip[1], tip[2] - 0.3);
      line(k.g, a[0], a[1], b[0], b[1], '#e0304a');
      box(k.g, b[0], b[1] - 1, 1, 1, '#e0304a');
      box(k.g, b[0], b[1] + 1, 1, 1, '#e0304a');
    }
    k.eyes([skull.f + 0.5 * HS, hl, skull.u + 0.4], 1.2 * HS, S.eye[0], S.eye[1], squint);
  });
  return k.finish();
}

const serpentSet = (S: SerpentSpec, h: number) =>
  beastViews((pose, ph, view) => serpent(S, pose, ph, view), { ax: S.frame[2], ay: S.frame[3], h });

/** งูพิษหยก: a slender jade viper banded in darker green, rearing to strike. */
const VIPER: SerpentSpec = {
  back: fur('#123a26', '#2f8a5a', '#7fe0a0'),
  band: fur('#0c2a1c', '#1d5e3c', '#4fb07a'),
  n: 13,
  gap: 1.6,
  r: 2,
  rear: 6,
  head: 1.35,
  eye: ['#ffe03a', '#ff7a1a'],
  frame: [44, 32, 22, 26],
};

/** งูเหลือมยักษ์: a thick python, olive blotched with dark brown. */
const PYTHON: SerpentSpec = {
  back: fur('#3a301a', '#7a6a3a', '#b4a46a'),
  band: fur('#1e160c', '#45341e', '#6a5432'),
  n: 15,
  gap: 2,
  r: 2.8,
  rear: 6,
  head: 1.9,
  eye: ['#ffa21a', '#ff4a1a'],
  frame: [58, 38, 29, 31],
};

/** ตะขาบพิษร้อยขา: a crimson-black centipede on a hundred yellow legs. */
const CENTIPEDE: SerpentSpec = {
  back: fur('#2a0a0c', '#6a1a1c', '#a8402e'),
  band: fur('#160608', '#3a0e10', '#e09a3a'),
  n: 12,
  gap: 2,
  r: 1.8,
  rear: 1.6,
  head: 1.1,
  eye: ['#ff3a2a', '#ffd23a'],
  legs: '#b8761e',
  frame: [54, 34, 27, 27],
};

// ── Arthropods ──

/** แมงป่องหางเพลิง: an armoured scorpion, pincers raised, its tail ending in a glowing sting. */
function scorpion(pose: BeastPose, ph: number, view: View): HTMLCanvasElement {
  const k = new Sculpt(view, 44, 38, 22, 30);
  const shell = fur('#1a0a0c', '#4a1a1e', '#8a3a32');
  const tailFur = fur('#4a160c', '#a8401a', '#ffa03a');
  let bodyF = 0;
  let claw = 0;
  let open = 0.4;
  let curl = 0;
  let sting = 0;
  let squint = false;
  if (pose === 'ready') {
    claw = 2 + odd(ph) * 0.5;
    open = 1;
    curl = 0.5;
  } else if (pose === 'windup') {
    claw = 3;
    open = 1.2;
    curl = 1.4;
    bodyF = -1;
  } else if (pose === 'strike') {
    claw = 1.5;
    open = 0.2;
    sting = 1;
    bodyF = 1.2;
  } else if (pose === 'hurt') {
    claw = -0.5;
    open = 0;
    curl = -0.6;
    bodyF = -1.2;
    squint = true;
  } else if (pose === 'idle') {
    claw = odd(ph) * 0.3;
  }
  const bob = pose === 'walk' ? odd(ph) * 0.4 : 0;
  const head = part(2.4 + bodyF, 0, 3.2 + bob, 2.6, 2.5, 1.5, shell);
  const body = [
    part(-0.6 + bodyF, 0, 3.2 + bob, 2.4, 2.9, 1.6, shell),
    part(-3 + bodyF, 0, 3.4 + bob, 2, 2.5, 1.5, shell),
    head,
  ];
  // Legs: four a side, stepping in alternation.
  for (const side of [1, -1]) {
    const legs = [2, 0.6, -0.8, -2.2].map((f, i) => {
      const step = pose === 'walk' ? (i % 2 === (side > 0 ? 0 : 1) ? 1 : -1) * Math.cos(turn(ph)) * 0.9 : 0;
      return [
        [f + bodyF, side * 2, 3],
        [f + bodyF + step * 0.5 + (i < 2 ? 0.6 : -0.6), side * 4.2, 4.6],
        [f + bodyF + step + (i < 2 ? 1.4 : -1.4), side * 5.8, 0],
      ] as P3[];
    });
    k.add(bodyF, side * 3, () => {
      for (const leg of legs) k.limb(leg, shell.d, 2);
    });
  }
  // Pincers: arm, then a heavy claw with two fingers.
  for (const side of [1, -1]) {
    const sh: P3 = [3.4 + bodyF, side * 1.8, 3.2];
    const el: P3 = [5.4 + bodyF + claw * 0.3, side * 3.8, 3.8 + claw * 0.6];
    const hand = part(7.4 + bodyF + claw * 0.4, side * 3, 4.4 + claw, 1.7, 1.1, 1.2, shell);
    k.add(6 + bodyF, side * 3.2, () => {
      k.limb([sh, el, [hand.f - 1, hand.l, hand.u]], shell.d, 2);
      k.shade([hand]);
      const tipF = hand.f + hand.rf + 1;
      k.seg([hand.f + 0.8, hand.l, hand.u + 0.4], [tipF, hand.l - side * 0.3, hand.u + 0.4 + open], shell.l);
      k.seg(
        [hand.f + 0.8, hand.l, hand.u - 0.4],
        [tipF, hand.l - side * 0.3, hand.u - 0.4 - open * 0.5],
        shell.m,
      );
    });
  }
  // Tail: segments arching up over the back toward the sting, which glows.
  const arc: P3[] = [
    [-5, 0, 4],
    [-6.6, 0, 5.8],
    [-7.2, 0, 8.2],
    [-6.4, 0, 10.4],
    [-4.6, 0, 11.8],
    [-2.6, 0, 12.2],
  ];
  const tail = arc.map(([f, l, u], i) => {
    const t = i / (arc.length - 1);
    const fwd = sting * t * t * 5 + curl * t * 2;
    const down = sting * t * t * 3 - curl * t * 1.2;
    return part(
      f + bodyF + fwd,
      l,
      u + bob - down,
      1.4 - t * 0.3,
      1.4 - t * 0.3,
      1.3 - t * 0.3,
      i > 3 ? tailFur : shell,
    );
  });
  const end = tail[tail.length - 1]!;
  const bulb = part(end.f + 1.4, 0, end.u - 0.6, 1.2, 1, 1.1, tailFur);
  k.add(-3 + bodyF, 0.1, () => {
    k.shade(body);
    k.eyes([head.f + 1.2, 0, head.u + 0.8], 0.7, '#ff3a2a', '#ffd23a', squint);
  });
  k.add(-5 + bodyF, -0.2, () => {
    k.shade([...tail, bulb]);
    k.seg([bulb.f + 0.8, 0, bulb.u - 0.4], [bulb.f + 1.8, 0, bulb.u - 1.6 - sting], '#ffe07a');
    k.dot([bulb.f + 1.8, 0, bulb.u - 1.6 - sting], '#fff6c0');
  });
  return k.finish();
}

/** แมงมุมหน้าผี: a giant spider on long jointed legs, a pale skull drawn on its back. */
function spider(pose: BeastPose, ph: number, view: View): HTMLCanvasElement {
  const k = new Sculpt(view, 46, 40, 23, 32);
  const shell = fur('#16101e', '#3a2e48', '#6a5a82');
  const legCol = '#1a1422';
  let bodyF = 0;
  let raise = 0;
  let fang = 0;
  let curl = 0;
  let squint = false;
  if (pose === 'ready') {
    raise = 1 + odd(ph) * 0.3;
    fang = 0.5;
  } else if (pose === 'windup') {
    raise = 1.8;
    fang = 1;
    bodyF = -1;
  } else if (pose === 'strike') {
    raise = 0.4;
    fang = 1.2;
    bodyF = 2;
  } else if (pose === 'hurt') {
    curl = 1;
    bodyF = -1;
    squint = true;
  }
  const bob = pose === 'walk' ? odd(ph) * 0.5 : 0;
  const abdomen = part(-4 + bodyF, 0, 6.4 + bob, 4.4, 4, 3.8, shell);
  const thorax = part(0.8 + bodyF, 0, 5.4 + bob, 2.6, 2.4, 2, shell);
  const head = part(2.8 + bodyF, 0, 5.4 + bob, 1.4, 1.8, 1.4, shell);
  for (const side of [1, -1]) {
    const legs = [0, 1, 2, 3].map((i) => {
      const spread = [2.8, 1, -1, -2.8][i]!;
      const front = i === 0 ? raise : i === 1 ? raise * 0.4 : 0;
      const step = pose === 'walk' ? ((i + (side > 0 ? 0 : 1)) % 2 ? 1 : -1) * Math.cos(turn(ph)) * 1.1 : 0;
      const pull = curl * 2.5;
      return [
        [1.4 - i * 0.9 + bodyF, side * 1.8, 5.6 + bob],
        [1.4 + spread * 1.2 + bodyF + step * 0.5, side * (5.2 - pull), 10 + front * 2.5 - curl],
        [1.4 + spread * 2.4 + bodyF + step + front * 1.5, side * (7.2 - pull * 1.6), front * 4 + curl * 3],
      ] as P3[];
    });
    k.add(bodyF, side * 4, () => {
      for (const leg of legs) k.limb(leg, legCol, 2);
      for (const leg of legs) k.dot(leg[1]!, shell.l);
    });
  }
  k.add(bodyF - 1, 0, () => {
    k.shade([abdomen, thorax, head]);
    if (k.faces(0, 0, 1) > 0.2) {
      // The pale skull pattern on top of the abdomen.
      const top = abdomen.u + abdomen.ru * 0.7;
      const a = abdomen.f;
      for (const [f, l] of [
        [0.6, -1.2],
        [0.6, 1.2],
        [1.4, 0],
        [-0.4, -1.6],
        [-0.4, 1.6],
        [-1.2, -0.6],
        [-1.2, 0.6],
        [-1.8, 0],
      ] as const)
        k.dot([a + f, l, top], '#d8d0c0');
      k.dot([a + 0.4, -0.7, top + 0.2], '#16101a');
      k.dot([a + 0.4, 0.7, top + 0.2], '#16101a');
    }
    if (k.sa < -0.2) return;
    // Fangs and a cluster of red eyes.
    for (const s of [1, -1]) {
      k.seg(
        [head.f + 0.8, s * 0.7, head.u - 0.6],
        [head.f + 1.4 + fang * 0.6, s * (0.5 - fang * 0.3), head.u - 2 - fang],
        '#3a2c44',
      );
      k.dot([head.f + 1.4 + fang * 0.6, s * (0.5 - fang * 0.3), head.u - 2 - fang], '#e8e2d2');
    }
    k.eyes([head.f + 1, 0, head.u + 0.9], 0.8, '#ff2a3a', '#ff8a5a', squint);
    k.eyes([head.f + 0.6, 0, head.u + 1.6], 0.5, '#ff2a3a', '#c01020', squint);
  });
  return k.finish();
}

// ── Flyers ──

/** ค้างคาวโลหิต: a blood bat hovering on ragged wings, ears up and fangs out. */
function bat(pose: BeastPose, ph: number, view: View): HTMLCanvasElement {
  const k = new Sculpt(view, 40, 38, 20, 34, FLYER_ANGLE);
  const body = fur('#1e0a10', '#4a1a26', '#7a3040');
  const skin = '#3a121c';
  const bone = '#7a3a48';
  // Wing beat: tips up (1) to down (-1).
  // Wings down and up through the beat (1, 0.2, −1, 0.2 on the old steps), the body rising with it.
  let flap = Math.cos(turn(ph)) * 0.9 + 0.1 + 0.1 * Math.sin(turn(ph)) ** 2;
  let hover = 14 + half(ph);
  let fwd = 0;
  let sweep = 0;
  let jaw = 0;
  let squint = false;
  if (pose === 'idle') flap = 0.1 + Math.cos(turn(ph)) * 0.7 + 0.2 * Math.sin(turn(ph)) ** 2;
  else if (pose === 'ready') jaw = 1;
  else if (pose === 'windup') {
    flap = 1.5;
    hover = 16;
    fwd = -1.5;
    jaw = 1.5;
  } else if (pose === 'strike') {
    flap = -0.4;
    hover = 10;
    fwd = 3;
    sweep = 2.5;
    jaw = 2;
  } else if (pose === 'hurt') {
    flap = -1.4;
    hover = 12;
    fwd = -1.5;
    sweep = -1;
    squint = true;
  }
  const torso = part(fwd, 0, hover, 1.6, 1.9, 2.4, body);
  const head = part(fwd + 1.1, 0, hover + 2.7, 1.5, 1.8, 1.5, body);
  for (const side of [1, -1]) {
    const w = (p: P3): P3 => [p[0] + fwd, p[1] * side, p[2] + hover];
    const sh = w([0.4, 1.4, 1.2]);
    const wrist = w([0.4 - sweep * 0.5, 5, 2.2 + flap * 2]);
    const tips = [
      w([-0.8 - sweep, 10, 1.4 + flap * 4]),
      w([-2.6 - sweep, 8.6, -1.6 + flap * 3]),
      w([-2.4 - sweep * 0.8, 5.4, -3.4 + flap * 1.6]),
    ];
    const hip = w([-0.6, 1.2, -1.8]);
    k.add(fwd - 1, side * 5, () => {
      // Ragged membrane between the fingers, the bones over it.
      k.fill(
        [
          sh,
          wrist,
          tips[0]!,
          w([-2 - sweep, 9, flap * 3.6]),
          tips[1]!,
          w([-2.8 - sweep, 6.8, -2.8 + flap * 2.2]),
          tips[2]!,
          hip,
        ],
        skin,
      );
      for (const t of tips) k.seg(wrist, t, bone);
      k.seg(sh, wrist, bone);
      k.dot([wrist[0] + 0.4, wrist[1], wrist[2] + 0.8], '#d8c8c0');
    });
  }
  k.add(fwd, 0, () => {
    // Feet tucked up under the body.
    for (const s of [1, -1])
      k.seg([fwd - 0.4, s * 0.8, hover - 2.2], [fwd - 0.6, s * 0.9, hover - 3.4], '#120608');
    k.shade([torso, head]);
    for (const s of [1, -1]) {
      const ear = part(head.f - 0.2, s * 1.1, head.u + 1.8, 0.6, 0.6, 1.4, body);
      k.shade([ear]);
      k.dot([ear.f, ear.l, ear.u + 1.6], body.d);
    }
    if (k.sa < -0.2) return;
    const [x, y] = k.at(head.f + 1.3, 0, head.u - 0.6);
    box(k.g, x - (k.ca < 0.5 ? 1 : 0), y, k.ca < 0.5 ? 2 : 1, 1, '#2a0a12');
    if (jaw > 0)
      box(k.g, x - (k.ca < 0.5 ? 1 : 0), y + 1, k.ca < 0.5 ? 2 : 1, Math.ceil(jaw * 0.5), '#8a1e2e');
    for (const s of [1, -1]) {
      if (k.faces(0.6, 0.8 * s) < -0.3 && k.ca > 0.2) continue;
      k.dot([head.f + 1.2, s * 0.6, head.u - 1.2 - jaw * 0.5], '#f4efe2', 1, 2);
    }
    k.eyes([head.f + 0.9, 0, head.u + 0.3], 0.9, '#ff3040', '#ffb0a0', squint);
  });
  return k.finish();
}

// ── The roster ──

/** Every sculpted creature's sprite set, keyed by sprite id. */
export function buildBestiary(
  snowify: (c: HTMLCanvasElement) => HTMLCanvasElement,
): Record<string, SpriteSet> {
  // Each creature set up the first time it is asked for.
  return lazyRecord({
    wolf: () => quadSet(WOLF, 24),
    snowwolf: () => quadSet(WOLF, 24, snowify),
    boar: () => quadSet(BOAR, 20),
    leopard: () => quadSet(LEOPARD, 22),
    whiteTiger: () => quadSet(TIGER, 30),
    foxSpirit: () => quadSet(FOX_SPIRIT, 24),
    viper: () => serpentSet(VIPER, 14),
    python: () => serpentSet(PYTHON, 18),
    centipede: () => serpentSet(CENTIPEDE, 12),
    scorpion: () => beastViews(scorpion, { ax: 22, ay: 30, h: 16 }),
    spider: () => beastViews(spider, { ax: 23, ay: 32, h: 16 }),
    bloodBat: () => beastViews(bat, { ax: 20, ay: 34, h: 24 }),
  });
}

/** Names for the studio, in the order it lists them. */
export const BESTIARY_LABELS: readonly [string, string][] = [
  ['wolf', 'หมาป่า'],
  ['snowwolf', 'หมาป่าหิมะ'],
  ['boar', 'หมูป่าเขี้ยวเหล็ก'],
  ['viper', 'งูพิษหยก'],
  ['scorpion', 'แมงป่องหางเพลิง'],
  ['leopard', 'เสือดาวเงา'],
  ['bloodBat', 'ค้างคาวโลหิต'],
  ['spider', 'แมงมุมหน้าผี'],
  ['centipede', 'ตะขาบพิษร้อยขา'],
  ['python', 'งูเหลือมยักษ์'],
  ['foxSpirit', 'จิ้งจอกเก้าหาง'],
  ['whiteTiger', 'พยัคฆ์ขาวภูผา'],
];
